import {
  CONTINUE_ON_OUTCOMES,
  DEFAULT_STOP_ON,
  QUIET_HOURS_FLOOR,
  WINDOW_DAYS,
  campaignSpecSchema,
  defaultAudience,
  describeLimits as describeSpecLimits,
  formatDuration,
  MAX_STEP_SLOTS,
  slotHoursWithin,
  stepHoursWithin,
  type Audience,
  type CampaignSpec,
  type ClockRange,
  type OutreachWindow,
  type ReferralReadiness,
  type SequenceStep,
  type StepSlot,
  type StepTiming,
  type TimingBand,
  type WindowDay,
} from '@vagusx/shared';

/**
 * Spec construction for the campaign editor.
 *
 * Pure, so the interesting parts are testable without rendering anything.
 *
 * **Prose lives in `@vagusx/shared`**, not here. The editor is no longer the
 * only thing that describes a campaign: the text panel renders the same
 * sentences, and the compiler is handed them as the statement of what it is
 * editing. A second vocabulary in the web app would be a second answer to
 * "what does this campaign do?", and the two would drift. What stays here is
 * what only an editor needs — construction, reordering, and the compact
 * summaries a list column has room for.
 */

const HOUR = 60;
const DAY = 24 * HOUR;

/** `4 hours`, `1 day`, `straight away`. */
export const formatMinutes = formatDuration;

export { describeDays, describeSuccess, describeWindow, SUCCESS_LABELS } from '@vagusx/shared';

/** A fresh step, with an id stable enough to survive reordering. */
export function newStep(existing: readonly SequenceStep[]): SequenceStep {
  // Ids are referenced by every attempt ever recorded, so they must never be
  // regenerated from position — `step-2` moving to the top would silently
  // re-point history. Take the next number no existing step is using.
  const used = new Set(existing.map((s) => s.id));
  let n = existing.length + 1;
  while (used.has(`step-${n}`)) n++;

  return sequenceStep({
    id: `step-${n}`,
    label: `Step ${existing.length + 1}`,
    // A step appended to the sequence is never the first, so it always starts
    // a set time after the one before it.
    trigger: { kind: 'after_previous', minutes: DAY },
    repeat: { times: 1, spacingMinutes: 0 },
  });
}

function sequenceStep(over: {
  id: string;
  label: string;
  trigger: SequenceStep['trigger'];
  repeat: { times: number; spacingMinutes: number };
  onMachine?: SequenceStep['onMachine'];
}): SequenceStep {
  return {
    id: over.id,
    label: over.label,
    channel: 'voice',
    action: { kind: 'call' },
    trigger: over.trigger,
    repeat: over.repeat,
    resolve: { kind: 'on_call_end' },
    // Never authored (R1): every step carries the same list, and only
    // `afterLastTry` and the campaign's `stopOn` end a sequence.
    continueOn: [...CONTINUE_ON_OUTCOMES],
    window: null,
    onMachine: over.onMachine ?? { kind: 'hang_up' },
    timeOfDay: 'fixed',
    onCallbackRequested: 'use_requested_time',
    // The author picks the workflow; publish pins its version (P1).
    workflow: null,
    agentId: null,
    afterLastTry: 'next',
    // A new step's calls spread across the window (ADR-318, "When to call").
    timing: { mode: 'auto' },
  };
}

/**
 * The spec a brand-new campaign opens with.
 *
 * Two steps rather than one: a single step reads as "the sequence is the
 * campaign" and hides the wait-then-continue mechanic that the whole editor is
 * about. The last step leaves a message, because a message on the first attempt
 * burns the only shot you get while on the last it is the entire point.
 */
export function defaultSpec(audienceType: Parameters<typeof defaultAudience>[0]): CampaignSpec {
  return campaignSpecSchema.parse({
    version: 1,
    audience: defaultAudience(audienceType),
    sequence: [
      sequenceStep({
        id: 'step-1',
        label: 'First attempt',
        // The first step starts the moment the patient matches the audience.
        trigger: { kind: 'qualifies' },
        repeat: { times: 2, spacingMinutes: 4 * HOUR },
      }),
      sequenceStep({
        id: 'step-2',
        label: 'Last try',
        trigger: { kind: 'after_previous', minutes: DAY },
        repeat: { times: 1, spacingMinutes: 0 },
        onMachine: { kind: 'leave_message', slot: 'default' },
      }),
    ],
    success: { kind: 'appointment_booked' },
    window: {
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      start: '09:00',
      end: '17:00',
      useLocationHours: false,
    },
    stopOn: [...DEFAULT_STOP_ON],
    maxTotalAttempts: 6,
    expiresAfterDays: 30,
  });
}

/**
 * Move `from` to `to`, returning a new array.
 *
 * Out-of-range indices return an unchanged copy rather than throwing: a drop
 * that lands on a step deleted in another tab must not take the editor down
 * with it.
 */
export function reorder<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** `Call ×2`, `Text`, `Call + message`. */
export function describeStep(step: SequenceStep): string {
  const verb = step.channel === 'sms' ? 'Text' : 'Call';
  const times = step.repeat.times > 1 ? ` ×${step.repeat.times}` : '';
  const message =
    step.channel === 'voice' && step.onMachine.kind === 'leave_message' ? ' + message' : '';
  return `${verb}${times}${message}`;
}

/** The campaign list's sequence column: `Call ×2 · Call + message`. */
export function summariseSequence(spec: CampaignSpec | null): string {
  if (!spec) return '—';
  return spec.sequence.map(describeStep).join(' · ');
}

/** What starts the step: `when the patient qualifies` / `1 day after First attempt`. */
export function describeTrigger(step: SequenceStep, previousLabel?: string): string {
  if (step.trigger.kind === 'qualifies') return 'when the patient qualifies';
  const after = previousLabel ? ` after ${previousLabel}` : ' after the previous step';
  return step.trigger.minutes === 0
    ? `immediately${after}`
    : `${formatMinutes(step.trigger.minutes)}${after}`;
}

/**
 * Make the sequence's triggers legal for the positions the steps now hold.
 *
 * Only the first step may be triggered by qualification, so reordering,
 * inserting or deleting can leave a trigger in a position where it means
 * nothing — and `campaignSpecSchema` rejects that, which would surface as an
 * unpublishable campaign with no visible cause. Applied after every structural
 * change to the list rather than validated afterwards.
 *
 * A step promoted to first loses its delay, because nothing precedes it any
 * more. That is the one piece of information this can discard, and it is
 * genuinely meaningless in the new position.
 */
export function normaliseTriggers(sequence: readonly SequenceStep[]): SequenceStep[] {
  return sequence.map((step, index) => {
    if (index === 0) {
      return step.trigger.kind === 'qualifies'
        ? step
        : { ...step, trigger: { kind: 'qualifies' as const } };
    }
    return step.trigger.kind === 'after_previous'
      ? step
      : { ...step, trigger: { kind: 'after_previous' as const, minutes: DAY } };
  });
}

/**
 * `6 calls · gives up after 30 days · priority 50`.
 *
 * Priority is not part of the spec — it is a column on the campaign row — so it
 * cannot come from the shared describer, which only ever states what the
 * versioned document says. This is the one summary that spans both.
 */
export function describeLimits(spec: CampaignSpec, priority: number): string {
  return `${describeSpecLimits(spec)} · priority ${priority}`;
}

// ───────────────────────── ADR-318, G: the editor's own fields ─────────────────────────

/**
 * Every step's `continueOn`, as the editor writes it on every change (R1). The
 * field is never shown: a step with three tries and an empty list stops after
 * its first, and nobody reading "Then: nothing" would guess that. Publish
 * rewrites it too; writing it here keeps the text panel and the preview honest
 * about the spec that will be published.
 */
export function withContinueOn(spec: CampaignSpec): CampaignSpec {
  const continueOn = [...CONTINUE_ON_OUTCOMES];
  const same = (step: SequenceStep): boolean =>
    step.continueOn.length === continueOn.length &&
    continueOn.every((o) => step.continueOn.includes(o));
  if (spec.sequence.every(same)) return spec;
  return { ...spec, sequence: spec.sequence.map((s) => (same(s) ? s : { ...s, continueOn })) };
}

/**
 * "When to call", as the editor shows it. `legacy` is a step published before
 * `timing` existed: its `timeOfDay` and step `window` still govern it, so it is
 * shown as what it is rather than relabelled — the schema keeps `timing` null
 * for exactly this, so its calls do not quietly move.
 */
export type WhenToCall = 'auto' | 'defined' | 'legacy';

export function whenToCall(step: SequenceStep): WhenToCall {
  return step.timing?.mode ?? 'legacy';
}

/** The labels for a defined step's preset parts of the day. */
export const BAND_LABELS: Record<TimingBand, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  late_afternoon: 'Late afternoon',
  any: 'All calling hours',
};

/** A clinic-local `HH:mm` range. */
export type StepHours = ClockRange;

/**
 * The hours calls can actually go out on a calling day: the campaign's hours
 * inside the organization's quiet hours and the absolute floor, the same layers
 * the resolver intersects before a step narrows anything. Location hours and a
 * patient's preference are per call, so they are not here. The campaign's own
 * hours when the layers leave nothing (launch refuses that as `window_empty`).
 */
export function callingHours(
  campaign: Pick<OutreachWindow, 'start' | 'end'>,
  policy: { quietHoursStart: string; quietHoursEnd: string } | undefined
): StepHours {
  const starts = [QUIET_HOURS_FLOOR.start, campaign.start, policy?.quietHoursStart ?? '00:00'];
  const ends = [QUIET_HOURS_FLOOR.end, campaign.end, policy?.quietHoursEnd ?? '23:59'];
  const start = starts.reduce((a, b) => (b > a ? b : a));
  const end = ends.reduce((a, b) => (b < a ? b : a));
  return start < end ? { start, end } : { start: campaign.start, end: campaign.end };
}

/**
 * The hours one slot calls in, as its time inputs show them: a preset as what
 * it means against the calling hours today, null when it misses them; custom
 * hours as typed.
 */
export function slotHours(slot: StepSlot, calling: StepHours): StepHours | null {
  if (slot.band === 'custom') return slot.hours;
  return slotHoursWithin(slot, calling);
}

/**
 * The hours a step calls in today, within the calling hours, by the rule the
 * resolver uses (`stepHoursWithin`): its slots cut to them, or all of them
 * when no slot fits and the step may fall back (ADR-348). A step with no
 * `timing` has none of its own.
 */
export function stepCallHours(
  step: SequenceStep,
  calling: StepHours
): { hours: StepHours[]; fallsBack: boolean } {
  return step.timing ? stepHoursWithin(step.timing, calling) : { hours: [], fallsBack: false };
}

/** A defined step's slots; empty for any other. */
export function stepSlots(step: SequenceStep): StepSlot[] {
  return step.timing?.mode === 'defined' ? step.timing.slots : [];
}

/**
 * Switch a step between Automatic and Defined. Defined keeps the slots and
 * days chosen earlier in this session; a fresh one starts on mornings, every
 * calling day.
 */
export function setWhenToCall(step: SequenceStep, mode: 'auto' | 'defined'): SequenceStep {
  if (mode === 'auto') return { ...step, timing: { mode: 'auto' } };
  if (step.timing?.mode === 'defined') return step;
  return {
    ...step,
    timing: { mode: 'defined', slots: [{ band: 'morning', hours: null }], days: null },
  };
}

function withSlots(step: SequenceStep, slots: StepSlot[]): SequenceStep {
  if (step.timing?.mode !== 'defined') return step;
  return { ...step, timing: { ...step.timing, slots } };
}

/**
 * Pick a preset part of the day for one slot. It stays a BAND rather than
 * being copied into hours, so a later change to the campaign's calling hours
 * carries it along.
 */
export function setSlotBand(step: SequenceStep, index: number, band: TimingBand): SequenceStep {
  const slots = stepSlots(step);
  if (!slots[index]) return step;
  return withSlots(
    step,
    slots.map((slot, i) => (i === index ? { band, hours: null } : slot))
  );
}

/**
 * Set one slot's exact hours: it becomes `custom`. A range whose end is not
 * after its start is kept as typed (the inputs are mid-edit): the field says
 * why it will not save, and the schema refuses it if it is saved anyway.
 */
export function setSlotHours(step: SequenceStep, index: number, hours: StepHours): SequenceStep {
  const slots = stepSlots(step);
  if (!slots[index]) return step;
  return withSlots(
    step,
    slots.map((slot, i) => (i === index ? { band: 'custom', hours } : slot))
  );
}

/**
 * Add a slot: the first of Morning, Afternoon and Late afternoon the step does
 * not use yet, so a new slot is not a copy of one already there. Unchanged
 * when all three are in use (together they already cover the calling hours;
 * a fourth fits once one is made exact hours) or at {@link MAX_STEP_SLOTS}.
 */
export function addSlot(step: SequenceStep): SequenceStep {
  const slots = stepSlots(step);
  if (step.timing?.mode !== 'defined' || slots.length >= MAX_STEP_SLOTS) return step;
  const used = new Set(slots.map((s) => s.band));
  const band = PARTS_OF_THE_DAY.find((b) => !used.has(b));
  return band ? withSlots(step, [...slots, { band, hours: null }]) : step;
}

/** The presets a new slot is offered from, in the order they are offered. */
const PARTS_OF_THE_DAY: readonly TimingBand[] = ['morning', 'afternoon', 'late_afternoon'];

/** Remove a slot. The last one stays: a defined step calls in at least one. */
export function removeSlot(step: SequenceStep, index: number): SequenceStep {
  const slots = stepSlots(step);
  if (slots.length <= 1 || !slots[index]) return step;
  return withSlots(
    step,
    slots.filter((_, i) => i !== index)
  );
}

/** Days in calendar order, so a narrowed list compares and reads stably. */
function ordered(days: Iterable<WindowDay>): WindowDay[] {
  const set = new Set(days);
  return WINDOW_DAYS.filter((d) => set.has(d));
}

/**
 * The days a step calls on: its own narrowed list, else every calling day of
 * the campaign. Only ever a subset of the campaign's.
 */
export function stepDays(step: SequenceStep, campaignDays: readonly WindowDay[]): WindowDay[] {
  const own = step.timing?.mode === 'defined' ? step.timing.days : null;
  return own ? ordered(own.filter((d) => campaignDays.includes(d))) : ordered(campaignDays);
}

/**
 * Turn one day on or off for a defined step. ONLY NARROWS: a day the campaign
 * does not call on is ignored, the last day cannot be turned off, and a list
 * equal to the campaign's days is stored as `null` ("every calling day"), so a
 * later change to the campaign's days carries the step with it.
 */
export function toggleStepDay(
  step: SequenceStep,
  day: WindowDay,
  campaignDays: readonly WindowDay[]
): SequenceStep {
  if (step.timing?.mode !== 'defined' || !campaignDays.includes(day)) return step;
  const current = stepDays(step, campaignDays);
  const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
  if (next.length === 0) return step;
  const days = next.length === campaignDays.length ? null : ordered(next);
  return { ...step, timing: { ...step.timing, days } };
}

/**
 * Keep every defined step inside the campaign's days after those change. A
 * step whose own days no longer overlap the campaign's, or now equal them,
 * goes back to "every calling day" rather than being left unpublishable
 * (`step_days_outside_window`).
 */
export function fitStepDays(spec: CampaignSpec): CampaignSpec {
  const campaignDays = spec.window.days;
  let changed = false;
  const sequence = spec.sequence.map((step) => {
    const timing: StepTiming | null = step.timing;
    if (timing?.mode !== 'defined' || !timing.days) return step;
    const kept = ordered(timing.days.filter((d) => campaignDays.includes(d)));
    const days = kept.length === 0 || kept.length === campaignDays.length ? null : kept;
    if (days === timing.days || (days && days.join() === timing.days.join())) return step;
    changed = true;
    return { ...step, timing: { ...timing, days } };
  });
  return changed ? { ...spec, sequence } : spec;
}

/**
 * The "Ready to call when" rows a referral audience has (P5). Name and a
 * dialable phone are always required and have no flag; null for an audience
 * that has no readiness (only referrals do).
 */
export function readinessOf(spec: CampaignSpec): ReferralReadiness | null {
  return spec.audience.type === 'open_referral' ? spec.audience.readiness : null;
}

export function setReadiness(spec: CampaignSpec, patch: Partial<ReferralReadiness>): CampaignSpec {
  if (spec.audience.type !== 'open_referral') return spec;
  return {
    ...spec,
    audience: { ...spec.audience, readiness: { ...spec.audience.readiness, ...patch } },
  };
}
